// Shared status rules for the vendor item write surfaces (HUBX-8474).
//
// The collapsed Status control: which values exist, which can be picked right now, what
// each one means. The picked value travels as-is, as `status`; the server maps it onto the
// axes (ADR-0009 Rule 9). Plus the Status <-> Available coupling, which lives here rather
// than in a file of its own because it only exists BECAUSE the projection folds inventory
// into status — split apart, neither half reads.
//
// Pure: every rule takes what it needs and returns an answer. No @angular anything, and no
// import at all — the baseline is declared structurally so a caller holding an ItemResponse
// passes it straight in without this module depending on the contract.

export interface ISkuStatusOption {
  value: string;
  label: string;
  assignable: boolean;
}

/** What the rules compare against: the item AS READ. `ItemResponse` satisfies it
 *  structurally, so a caller holding one passes it straight in — the modal its `origin`,
 *  a row its entry in the page's Map (HUBX-8479).
 *
 *  Undefined means "no item read yet": a Create, or an Edit whose lookups have not landed.
 *  Every rule here answers the Create-safe way in that case rather than throwing, which is
 *  what lets one modal serve both modes without a mode flag in this module. */
export interface IStatusBaseline {
  vendorStatus: string;
  businessStatus: string;
  effectiveStatus: string;
  availability: number;
  unitPurchasePrice?: number | null;
  // The five a Seller may change on an Approved item at the price of a re-review (HUBX-8515).
  minimumOrderQty?: number | null;
  maximumOrderQty?: number | null;
  leadTimeDays?: number | null;
  specialNotes?: string | null;
  restrictions?: string | null;
}

/** Those five as the form holds them, under the control names (getRawValue() satisfies it):
 *  numbers or text, blank as '' or null. */
export interface IReviewedValues {
  moq?: unknown;
  mxq?: unknown;
  leadTimeDays?: unknown;
  specialNotes?: unknown;
  restrictions?: unknown;
}

/** The collapsed Status list. `value` is the EffectiveStatus the server projects; `label`
 *  is the wording the vendors already read — the only divergence is OutOfStock, shown as
 *  "Sold Out". `Pending` is the one entry legacy does not have: ADR-0009 Rule 7 splits it
 *  out of what legacy displayed as Inactive.
 *
 *  Three values are assignable. Active and Paused name a vendor intent directly. `Sold Out`
 *  is different in kind: it is a SHORTCUT for zeroing Available, which is how the inline
 *  grid row already works and what vendors use it for. It moves no status axis at all — as
 *  `status` it is an echo, and the server re-derives Sold Out from the inventory it now holds.
 *
 *  The rest are outcomes and stay in the list, disabled: dropping them would leave the
 *  select blank whenever the item holds one, and showing the item's real state is the point
 *  of this control.
 *
 *  The two groups are rendered as <optgroup>s, which is why this does not follow the legacy
 *  ORDER — the assignable values come first. A disabled <option> is greyed by the browser
 *  and nothing else, and how grey varies by browser and OS, so on its own it is too weak a
 *  signal. Grouping is the only native way to say WHY they differ rather than just that
 *  they do. */
export const SKU_STATUS_OPTIONS: ISkuStatusOption[] = [
  { value: 'Active', label: 'Active', assignable: true },
  { value: 'Paused', label: 'Paused', assignable: true },
  { value: 'OutOfStock', label: 'Sold Out', assignable: true },
  { value: 'Inactive', label: 'Inactive', assignable: false },
  { value: 'Pending', label: 'Pending', assignable: false },
  { value: 'Removed', label: 'Removed', assignable: false },
  { value: 'Archived', label: 'Archived', assignable: false }
];

/** Whether an option can be picked RIGHT NOW. Static for all but one: `Sold Out` is offered
 *  only on an item that is already sellable, because that is the only case where zeroing
 *  Available changes what a buyer sees. On a Pending or Removed item the projection would
 *  keep reporting that other state (SkuStatusProjection checks the axes before inventory),
 *  so the pick would silently not stick. */
export function isAssignable(option: ISkuStatusOption, baseline?: IStatusBaseline): boolean {
  if (option.value === 'OutOfStock') {
    const effective = baseline?.effectiveStatus;
    return effective === 'Active' || effective === 'OutOfStock';
  }
  return option.assignable;
}

export function assignableStatusOptions(baseline?: IStatusBaseline): ISkuStatusOption[] {
  return SKU_STATUS_OPTIONS.filter(o => isAssignable(o, baseline));
}

export function automaticStatusOptions(baseline?: IStatusBaseline): ISkuStatusOption[] {
  return SKU_STATUS_OPTIONS.filter(o => !isAssignable(o, baseline));
}

/** What the CURRENT status means, shown beside the closed select. The grouping only helps
 *  once the list is open; this is what a user reading the form sees, and it is the
 *  difference between "Sold Out" looking like a setting somebody chose and looking like
 *  what it is — a consequence of the Available field two columns away. */
export function skuStatusHint(value: string | null | undefined): string {
  switch (value) {
    case 'Active': return 'Live and buyable';
    case 'Paused': return 'Off the shelf, by you';
    case 'OutOfStock': return 'Available is 0';
    case 'Pending': return 'Awaiting HUBX review';
    case 'Inactive': return 'Switched off by the system';
    case 'Removed': return 'Removed by HUBX';
    case 'Archived': return 'Archived — use Restore';
    default: return '';
  }
}

/** Three tones, not two: green is sellable, amber is off the shelf by the vendor's own
 *  choice, grey is not the vendor's call at all. Collapsing the last two would make a
 *  deliberate pause look like the same kind of fact as a system deactivation. */
export function skuStatusTone(value: string | null | undefined): string {
  if (value === 'Active') {
    return '__live';
  }
  return value === 'Paused' ? '__held' : '__auto';
}

/** Whether this selection will be consumed as approval rather than as a vendor-axis move.
 *  `Active` is the only ambiguous option: on a Pending item it can mean either "approve" or
 *  "un-pause", and which one it is depends on the caller. A Seller picking `Active` there is
 *  un-pausing; an operator is approving.
 *
 *  The SERVER decides it (StatusIntentResolution, ADR-0009 Rule 9): the request carries the
 *  pick as `status` and nothing else. This only mirrors that rule for the notice — only FROM
 *  Pending and only on Active. */
export function isApproving(skuStatus: string, baseline: IStatusBaseline | undefined, mayApprove: boolean): boolean {
  return skuStatus === 'Active'
    && mayApprove
    && baseline?.businessStatus === 'Pending';
}

/** Whether saving would approve the item: a Pending item an operator has switched to Active.
 *  Drives the notice, so the approval is never silent — it is a consequence of choosing
 *  Active rather than something the user asked for by name, and the one irreversible thing
 *  this control can do. */
export function isApprovalPending(skuStatus: string, baseline: IStatusBaseline | undefined, mayApprove: boolean): boolean {
  return !!baseline && isApproving(skuStatus, baseline, mayApprove);
}

/** Whether an operator's Active approves (out of Pending) or reactivates (out of Removed) an
 *  item the vendor keeps paused (HUBX-8466). The server moves Business only and leaves the
 *  Vendor axis as stored (ADR-0009 Rule 6), so the save lands on Paused and a second Active
 *  publishes it. Business chose to keep the two saves and say so, before Save and after it,
 *  rather than lift the pause. Mirrors StatusIntentResolution for the wording only. */
export function staysPausedAfterSave(skuStatus: string, baseline: IStatusBaseline | undefined, mayApprove: boolean): boolean {
  return skuStatus === 'Active'
    && mayApprove
    && !!baseline
    && (baseline.businessStatus === 'Pending' || baseline.businessStatus === 'Removed')
    && baseline.vendorStatus === 'Pause';
}

/** Whether saving would send the item BACK for review: a Seller raising its cost (ADR-0009
 *  Rule 6, CostIncreaseReapproval), or a Seller changing MOQ, MXQ, ETA, Special Notes or
 *  Restrictions on an Approved item (HUBX-8515, ReviewedFieldReapproval; see
 *  changesReviewedMember). The mirror of isApprovalPending — same purpose, opposite
 *  direction, opposite actor — and the two are mutually exclusive by construction, since
 *  that one needs mayApprove and this one its absence.
 *
 *  It earns a notice for the same reason: the user did not ask for it in those words. They
 *  changed a value, and the item leaves the catalogue until an operator looks at it. Without
 *  this the vendor discovers it by the row vanishing from Active.
 *
 *  `reviewed` is the form's values: the modal and both listing rows pass getRawValue() (the
 *  inline row draws three of the five; the two it does not draw stay as read). Omitted, only
 *  the cost is read.
 *
 *  The baseline is the state the form was built against, which REBASES after a 412
 *  (HUBX-8439), so the notice speaks about the cost the server holds NOW rather than the one
 *  the surface opened on — otherwise a reload could leave it claiming a raise that is no
 *  longer one.
 *
 *  `mayApprove` stands in for the server's `isSeller`: it is the client's own role
 *  (ADMIN / VENDORMGR). writableFields cannot say it any more — it carries `status` for
 *  every role (ADR-0009 Rule 9) — and a notice may read the role (ADR-0011). The server's
 *  third condition — the item is not HUBX-owned — needs no counterpart: on such an item
 *  ItemWritability returns HubxOwnedWritePolicy's list, which carries no `unitPurchasePrice`
 *  for any role, so the control is disabled and the value cannot move.
 *
 *  A blank cost reports false, which the server does not do — there, a null STORED cost
 *  fires whatever the body carries. Deliberate: a notice about an empty field is noise, and
 *  that Save fails validation anyway. This is advisory, not the rule. */
export function isReviewPending(
  submittedCost: unknown,
  baseline: IStatusBaseline | undefined,
  mayApprove: boolean,
  reviewed?: IReviewedValues
): boolean {
  if (!baseline || mayApprove) {
    return false;
  }
  if (reviewed && baseline.businessStatus === 'Approved' && changesReviewedMember(reviewed, baseline)) {
    return true;
  }
  if (submittedCost == null || submittedCost === '') {
    return false;
  }
  const stored = baseline.unitPurchasePrice ?? null;
  return stored == null || Number(submittedCost) > stored;
}

/** Whether the form moves any of the five reviewed members (HUBX-8515) away from the item as
 *  read. Compared as the PUT will store them, so an untouched form never reads as a change:
 *  text trimmed and blank as none (buildEditRequest omits it); a blank MOQ as 1 and an MXQ of
 *  0 or blank as no maximum (OrderQuantities). Advisory, like every notice here: the server
 *  decides. */
export function changesReviewedMember(values: IReviewedValues, baseline: IStatusBaseline): boolean {
  const num = (value: unknown): number | null => value == null || value === '' ? null : Number(value);
  const max = (value: unknown): number | null => {
    const n = num(value);
    return n != null && n > 0 ? n : null;
  };
  const text = (value: unknown): string | null => String(value ?? '').trim() || null;

  return (num(values.moq) ?? 1) !== (baseline.minimumOrderQty ?? 1)
    || max(values.mxq) !== max(baseline.maximumOrderQty)
    || num(values.leadTimeDays) !== (baseline.leadTimeDays ?? null)
    || text(values.specialNotes) !== text(baseline.specialNotes)
    || text(values.restrictions) !== text(baseline.restrictions);
}

/** Whether saving would bring a Removed item BACK (HUBX-8476): the third sibling of
 *  isApprovalPending and isReviewPending. The rule itself is the server's
 *  (CostReductionReactivation.ShouldReactivate, HUBX-8367) and runs on the write path; this
 *  only tells the vendor, because they changed a number and the item returns to the shelf.
 *
 *  Legacy made the vendor pick "Active" and enabled that option only once the typed cost
 *  cleared the threshold (item-simple handleInternalStatuses / toggleDropdownOptionActivation).
 *  That gating does not port: under v2 `Removed` is system-only (ADR-0009 Rule 6) and the
 *  reactivation is derived, so there is no status to select. What survives is the
 *  information — a notice, not a control.
 *
 *  `threshold` is the one the listing row carries (`reactivationThreshold`, HUBX-8486), NOT
 *  computed here: that would mean publishing the cost-reduction bands to the client and
 *  keeping a second copy of the rule. It is truncated down to cents by the server, so a cost
 *  at or below it is always at or below the raw threshold the server compares against — the
 *  notice never promises a reactivation that does not happen. No threshold, no notice: the
 *  server sends none when the item has no stored cost or no band covers it.
 *
 *  The cost must be writable for this caller, read off writableFields like every permission
 *  here. Checked inline rather than through the writability module, which would pull
 *  @angular/forms into this import-free file.
 *
 *  A blank cost reports false — the same deliberate departure isReviewPending takes: a
 *  notice about an empty field is noise, and that Save fails validation anyway. A cost of
 *  zero or less reports false because the server never reactivates on one. Advisory, not
 *  the rule. */
export function isReactivationPending(
  submittedCost: unknown,
  baseline: IStatusBaseline | undefined,
  writableFields: string[],
  threshold: number | null | undefined
): boolean {
  if (!baseline || baseline.businessStatus !== 'Removed' || threshold == null) {
    return false;
  }
  if (!writableFields.includes('unitPurchasePrice')) {
    return false;
  }
  if (submittedCost == null || submittedCost === '') {
    return false;
  }
  const cost = Number(submittedCost);
  return cost > 0 && cost <= threshold;
}

/** Whether saving would reactivate a Removed item MANUALLY (HUBX-8476, ADR-0009 Rule 9): the
 *  fourth sibling. An operator choosing `Active` on a Removed item brings it back at any cost;
 *  the server does it (StatusIntentResolution) and acknowledges it with the
 *  MANUAL_REACTIVATION_BELOW_THRESHOLD warning. This says it before Save.
 *
 *  Only when the cost does NOT already reactivate it - isReactivationPending false, whether
 *  the cost sits above the threshold, the row carries none, or the cost is not this caller's
 *  to write. When the cut does reach it, the automatic notice is the one that holds and this
 *  one stays silent: the two never show together.
 *
 *  `mayApprove` is the client's own role (ADMIN / VENDORMGR), as for isApprovalPending: a
 *  Seller choosing `Active` without the cut is refused by the server
 *  (REACTIVATION_THRESHOLD_NOT_MET), so it never reaches a manual reactivation. The role only
 *  chooses the wording (ADR-0009 Consequences); the server decides.
 *
 *  A blank cost reports false - the departure the two cost notices take: a notice about an
 *  empty field is noise, and that Save fails validation anyway. */
export function isManualReactivationPending(
  skuStatus: string,
  submittedCost: unknown,
  baseline: IStatusBaseline | undefined,
  writableFields: string[],
  threshold: number | null | undefined,
  mayApprove: boolean
): boolean {
  if (!mayApprove || !baseline || baseline.businessStatus !== 'Removed' || skuStatus !== 'Active') {
    return false;
  }
  if (submittedCost == null || submittedCost === '') {
    return false;
  }
  return !isReactivationPending(submittedCost, baseline, writableFields, threshold);
}

// ---------------------------------------------------------------------------
// Status <-> Available, kept in step in BOTH directions — one fact behind two controls.
// ---------------------------------------------------------------------------

/** What Available becomes when Status is picked, or null for "leave it alone".
 *
 *  Picking Sold Out zeroes Available: that IS the shortcut, and it is why the value is
 *  assignable at all. Leaving it restores what was read, so a mis-click costs nothing — and
 *  only the zeroing marks the control dirty, because the restore is putting back a value the
 *  user never moved. */
export function availabilityForStatus(
  nextStatus: string,
  currentAvailability: unknown,
  baseline?: IStatusBaseline
): { availability: number; markDirty: boolean } | null {
  if (nextStatus === 'OutOfStock') {
    return { availability: 0, markDirty: true };
  }
  if (currentAvailability === 0 && baseline?.availability) {
    return { availability: baseline.availability, markDirty: false };
  }
  return null;
}

/** The reverse: typing a stock figure is the other way to say the same thing. Null means the
 *  status stays as it is.
 *
 *  Deliberately NOT paired with disabling the Available field, which is what legacy does —
 *  that would take away the most direct way back out of Sold Out, and it would fight
 *  applyWritability over who owns the control's enabled state. */
export function statusForAvailability(availability: unknown, currentStatus: string): string | null {
  if (Number(availability) === 0 && currentStatus === 'Active') {
    return 'OutOfStock';
  }
  if (Number(availability) > 0 && currentStatus === 'OutOfStock') {
    return 'Active';
  }
  return null;
}
